import {
  AlignCenterHorizontal,
  ArrowDownUp,
  Columns3,
  CopyPlus,
  Image as ImageIcon,
  Lock,
  MoreHorizontal,
  MousePointer2,
  Play,
  Ruler,
  Scaling,
  Shapes,
  Square,
  Trash2,
  Type,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  buildGalleryDoc,
  findGalleryEntry,
  resizeDoc,
  type Layer,
  type TemplateDoc,
} from '@bannermatic/core';
import { Scene } from '@bannermatic/scene';
import { resolvePreviewSrc, useBuiltinFonts } from '../../components/DocPreview';
import { cn } from '../../lib/format';
import { useLocale, useT } from '../../lib/i18n';

/** Gallery template whose picture crops well at every size below. */
const SHOWCASE_TEMPLATE = 'travel-banner';

const SIZES = [
  { id: 'wide', label: '1200 × 628', w: 1200, h: 628 },
  { id: 'square', label: '1080 × 1080', w: 1080, h: 1080 },
  { id: 'story', label: '1080 × 1920', w: 1080, h: 1920 },
  { id: 'half', label: '300 × 600', w: 300, h: 600 },
] as const;

const TYPE_ICON: Record<Layer['type'], typeof Type> = {
  text: Type,
  image: ImageIcon,
  shape: Square,
  qr: Shapes,
  group: Columns3,
};

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Ordered like the layers panel: children right under their stack. */
function layerTree(doc: TemplateDoc): Array<{ layer: Layer; depth: number }> {
  const out: Array<{ layer: Layer; depth: number }> = [];
  const walk = (parentId: string | undefined, depth: number) => {
    for (const l of [...doc.layers].reverse()) {
      if ((l.parentId ?? undefined) !== parentId) continue;
      out.push({ layer: l, depth });
      if (l.type === 'group') walk(l.id, depth + 1);
    }
  };
  walk(undefined, 0);
  return out;
}

/**
 * A working miniature of the editor on the landing page: the same template re-laid out for
 * several sizes by auto layout, a selection with quick actions and Alt-style distances.
 */
export function EditorShowcase() {
  const t = useT();
  const locale = useLocale((s) => s.locale);
  useBuiltinFonts();
  const base = useMemo(() => {
    const entry = findGalleryEntry(SHOWCASE_TEMPLATE)!;
    return buildGalleryDoc(entry, locale);
  }, [locale]);
  const [sizeId, setSizeId] = useState<(typeof SIZES)[number]['id']>('wide');
  const [auto, setAuto] = useState(true);
  const size = SIZES.find((s) => s.id === sizeId)!;
  const doc = useMemo(
    () =>
      size.w === base.width && size.h === base.height ? base : resizeDoc(base, size.w, size.h),
    [base, size.w, size.h],
  );
  const tree = useMemo(() => layerTree(doc), [doc]);
  const [selectedName, setSelectedName] = useState('title');
  const selected = doc.layers.find((l) => l.name === selectedName) ?? doc.layers[0];
  const parent = selected.parentId ? doc.layers.find((l) => l.id === selected.parentId) : null;

  // Cycle through the sizes until the visitor takes over
  useEffect(() => {
    if (!auto) return;
    const id = setInterval(
      () => setSizeId((s) => SIZES[(SIZES.findIndex((x) => x.id === s) + 1) % SIZES.length].id),
      3200,
    );
    return () => clearInterval(id);
  }, [auto]);

  // Fit the canvas into the stage
  const stage = useRef<HTMLDivElement>(null);
  const [stageSize, setStageSize] = useState({ w: 600, h: 460 });
  useEffect(() => {
    const el = stage.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setStageSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const pad = 40;
  const scale = Math.min((stageSize.w - pad * 2) / doc.width, (stageSize.h - pad * 2) / doc.height);
  const canvas = {
    w: doc.width * scale,
    h: doc.height * scale,
    x: (stageSize.w - doc.width * scale) / 2,
    y: (stageSize.h - doc.height * scale) / 2,
  };

  // Selection box measured from the rendered layer (stacks are laid out by the browser)
  const sceneRef = useRef<HTMLDivElement>(null);
  const [sel, setSel] = useState<Box | null>(null);
  useLayoutEffect(() => {
    const measure = () => {
      const root = sceneRef.current;
      const el = root?.querySelector<HTMLElement>(
        `[data-layer-name="${CSS.escape(selected.name)}"]`,
      );
      if (!root || !el) return setSel(null);
      const r = el.getBoundingClientRect();
      const o = root.getBoundingClientRect();
      setSel({ x: r.left - o.left, y: r.top - o.top, w: r.width, h: r.height });
    };
    measure();
    // Fonts and images settle a moment later
    const id = setTimeout(measure, 250);
    return () => clearTimeout(id);
  }, [doc, selected.name, scale]);

  const px = (v: number) => Math.round(v / scale);
  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 shadow-indigo-500/10 ring-zinc-200">
      {/* Title bar */}
      <div className="flex items-center gap-3 border-b border-zinc-200 bg-zinc-50 px-4 py-2.5">
        <div className="hidden gap-1.5 sm:flex">
          <span className="size-3 rounded-full bg-rose-400" />
          <span className="size-3 rounded-full bg-amber-400" />
          <span className="size-3 rounded-full bg-emerald-400" />
        </div>
        <span className="hidden truncate text-sm font-medium text-zinc-700 sm:block">
          {SHOWCASE_TEMPLATE}
        </span>
        <div
          className="mx-auto flex max-w-full min-w-0 gap-1 overflow-x-auto rounded-lg bg-zinc-200/70 p-0.5"
          data-testid="showcase-sizes"
        >
          {SIZES.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => {
                setAuto(false);
                setSizeId(s.id);
              }}
              className={cn(
                'rounded-md px-2 py-1 text-[11px] font-medium whitespace-nowrap tabular-nums transition sm:px-2.5 sm:text-xs',
                s.id === sizeId
                  ? 'bg-white text-zinc-900 shadow-sm'
                  : 'text-zinc-500 hover:text-zinc-800',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
        <span className="hidden items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white md:inline-flex">
          <Play className="size-3.5" /> {t('showcase.render')}
        </span>
      </div>

      <div className="grid md:grid-cols-[190px_minmax(0,1fr)] lg:grid-cols-[190px_minmax(0,1fr)_230px]">
        {/* Layers */}
        <div className="hidden border-r border-zinc-200 p-3 md:block">
          <div className="mb-2 px-1 text-[11px] font-semibold tracking-wider text-zinc-400 uppercase">
            {t('showcase.layers')}
          </div>
          <div className="space-y-0.5">
            {tree.map(({ layer, depth }) => {
              const Icon = TYPE_ICON[layer.type];
              return (
                <button
                  key={layer.id}
                  type="button"
                  onClick={() => setSelectedName(layer.name)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md py-1 pr-2 text-left text-xs transition',
                    layer.name === selected.name
                      ? 'bg-brand-50 font-medium text-brand-700'
                      : 'text-zinc-600 hover:bg-zinc-100',
                  )}
                  style={{ paddingLeft: 8 + depth * 14 }}
                >
                  <Icon className="size-3.5 shrink-0 opacity-70" />
                  <span className="truncate font-mono">{layer.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Canvas */}
        <div
          ref={stage}
          className="relative h-[380px] overflow-hidden bg-zinc-100 sm:h-[460px]"
          style={{
            backgroundImage: 'radial-gradient(#d4d4d8 1px, transparent 1px)',
            backgroundSize: '18px 18px',
          }}
        >
          <div
            className="absolute shadow-xl transition-[left,top,width,height] duration-500"
            style={{ left: canvas.x, top: canvas.y, width: canvas.w, height: canvas.h }}
          >
            <div
              ref={sceneRef}
              className="absolute top-0 left-0 overflow-hidden"
              style={{ width: canvas.w, height: canvas.h }}
              onClick={(e) => {
                const name = (e.target as HTMLElement).closest<HTMLElement>('[data-layer-name]')
                  ?.dataset.layerName;
                if (name) setSelectedName(name);
              }}
            >
              <div
                style={{
                  width: doc.width,
                  height: doc.height,
                  transform: `scale(${scale})`,
                  transformOrigin: '0 0',
                }}
              >
                <Scene doc={doc} mode="render" resolveSrc={resolvePreviewSrc} />
              </div>
            </div>
            {sel && (
              <>
                {/* Distances to the canvas edges, like holding Alt */}
                <Distance
                  x1={0}
                  y1={sel.y + sel.h / 2}
                  x2={sel.x}
                  y2={sel.y + sel.h / 2}
                  v={px(sel.x)}
                />
                <Distance
                  x1={sel.x + sel.w}
                  y1={sel.y + sel.h / 2}
                  x2={canvas.w}
                  y2={sel.y + sel.h / 2}
                  v={px(canvas.w - sel.x - sel.w)}
                />
                <Distance
                  x1={sel.x + sel.w / 2}
                  y1={0}
                  x2={sel.x + sel.w / 2}
                  y2={sel.y}
                  v={px(sel.y)}
                />
                <div
                  className="pointer-events-none absolute border-2 border-brand-500"
                  style={{ left: sel.x, top: sel.y, width: sel.w, height: sel.h }}
                >
                  {[
                    [0, 0],
                    [1, 0],
                    [0, 1],
                    [1, 1],
                  ].map(([cx, cy]) => (
                    <span
                      key={`${cx}${cy}`}
                      className="absolute size-2 rounded-sm border border-brand-500 bg-white"
                      style={{ left: `calc(${cx * 100}% - 5px)`, top: `calc(${cy * 100}% - 5px)` }}
                    />
                  ))}
                </div>
                {/* Quick actions */}
                <div
                  className="pointer-events-none absolute flex -translate-x-1/2 items-center gap-0.5 rounded-lg border border-zinc-200 bg-white p-0.5 shadow-lg"
                  style={{
                    left: sel.x + sel.w / 2,
                    top: sel.y >= 44 ? sel.y - 40 : sel.y + sel.h + 8,
                  }}
                >
                  {[Lock, CopyPlus, Trash2, MoreHorizontal].map((Icon, i) => (
                    <span
                      key={i}
                      className={cn(
                        'flex size-6 items-center justify-center rounded-md',
                        i === 2 ? 'text-rose-500' : 'text-zinc-600',
                      )}
                    >
                      <Icon className="size-3.5" />
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-medium text-zinc-600 shadow-sm">
            <MousePointer2 className="size-3" /> {t('showcase.clickHint')}
          </div>
        </div>

        {/* Properties */}
        <div className="hidden space-y-4 border-l border-zinc-200 p-4 text-xs lg:block">
          <div>
            <div className="text-[11px] font-semibold tracking-wider text-zinc-400 uppercase">
              {t('showcase.layer')}
            </div>
            <div className="mt-1 font-mono text-sm font-semibold text-zinc-900">
              {selected.name}
            </div>
            <div className="mt-0.5 text-zinc-500">{t('showcase.apiField')}</div>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {(
              [
                ['W', sel ? px(sel.w) : Math.round(selected.width)],
                ['H', sel ? px(sel.h) : Math.round(selected.height)],
              ] as const
            ).map(([k, v]) => (
              <div key={k} className="rounded-md border border-zinc-200 px-2 py-1.5 tabular-nums">
                <span className="mr-1.5 text-zinc-400">{k}</span>
                {v}
              </div>
            ))}
          </div>
          {selected.type === 'text' && (
            <div className="space-y-1.5">
              <div className="rounded-md border border-zinc-200 px-2 py-1.5">
                {selected.fontFamily}
              </div>
              <div className="flex gap-1.5">
                <div className="flex-1 rounded-md border border-zinc-200 px-2 py-1.5 tabular-nums">
                  {Math.round(selected.fontSize)} px
                </div>
                <div className="flex items-center gap-1.5 rounded-md border border-zinc-200 px-2 py-1.5">
                  <span
                    className="size-3 rounded-sm ring-1 ring-zinc-200"
                    style={{ background: selected.color }}
                  />
                  <span className="font-mono">{selected.color.slice(0, 7)}</span>
                </div>
              </div>
            </div>
          )}
          {parent?.type === 'group' && (
            <div className="rounded-xl bg-violet-50 p-3 ring-1 ring-violet-100">
              <div className="flex items-center gap-1.5 font-semibold text-violet-700">
                <Columns3 className="size-3.5" /> {t('showcase.stack')}
              </div>
              <div className="mt-2 grid grid-cols-2 gap-1.5 text-violet-900">
                <span className="flex items-center gap-1">
                  <ArrowDownUp className="size-3" />
                  {parent.direction === 'horizontal'
                    ? '→'
                    : parent.direction === 'auto'
                      ? 'auto'
                      : '↓'}
                </span>
                <span className="tabular-nums">gap {Math.round(parent.gap)}</span>
              </div>
            </div>
          )}
          <div className="rounded-xl bg-sky-50 p-3 ring-1 ring-sky-100">
            <div className="flex items-center gap-1.5 font-semibold text-sky-700">
              <Scaling className="size-3.5" /> {t('showcase.resize')}
            </div>
            <p className="mt-1 leading-relaxed text-sky-900/80">{t('showcase.resizeText')}</p>
          </div>
          <div className="flex items-center gap-2 text-zinc-500">
            <Ruler className="size-3.5 text-rose-500" /> {t('showcase.altHint')}
            <AlignCenterHorizontal className="ml-auto size-3.5 text-zinc-300" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Distance({
  x1,
  y1,
  x2,
  y2,
  v,
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  v: number;
}) {
  if (v < 4) return null;
  const horizontal = y1 === y2;
  return (
    <div className="pointer-events-none absolute" style={{ left: 0, top: 0 }}>
      <div
        className="absolute bg-rose-500"
        style={
          horizontal
            ? { left: x1, top: y1, width: x2 - x1, height: 1 }
            : { left: x1, top: y1, width: 1, height: y2 - y1 }
        }
      />
      <span
        className="absolute -translate-x-1/2 -translate-y-1/2 rounded bg-rose-500 px-1 py-px text-[10px] font-semibold text-white tabular-nums"
        style={{ left: (x1 + x2) / 2, top: (y1 + y2) / 2 }}
      >
        {v}
      </span>
    </div>
  );
}
